import classNames from 'classnames';
import isEqual from 'lodash.isequal';
import type { ChangeEvent, FormEvent } from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { iconBookmark } from '@sorare/core/src/atoms/icons/system/iconBookmark.ts';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus.ts';
import { iconTrash } from '@sorare/core/src/atoms/icons/system/iconTrash.ts';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';

import type { CardFilters } from './updateCardFilters.ts';
import {
  UPDATE_SAVED_FILTERS_MUTATION,
  toTrackingFilters,
} from './updateCardFilters.ts';

const DropdownContent = styled.div`
  width: 240px;
`;
const Row = styled.div`
  height: 56px;
  display: flex;
  justify-content: space-between;
  align-items: stretch;

  &:hover {
    background-color: var(--c-nd-100);
  }

  & > *:first-child {
    flex: 1;
    overflow: hidden;
  }
`;
const StyledButton = styled(ButtonBase)`
  text-align: left;
  padding: 0 var(--double-unit);
  color: var(--c-nd-600);

  &.active {
    color: var(--c-blue-300);
  }
`;
const AddButton = styled(StyledButton)`
  width: 100%;
  height: 56px;
  display: inline-flex;
  align-items: center;
  gap: var(--unit);
  color: var(--c-white);

  &:not(:disabled):hover {
    background-color: var(--c-nd-100);
  }

  &:disabled {
    color: var(--c-nd-200);
    cursor: initial;
  }
`;
const DeleteButton = styled(StyledButton)`
  width: 46px;
  color: var(--c-nd-400);

  &:hover {
    color: var(--c-white);
  }
`;
const DialogForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  margin-bottom: 0;
  padding: 0 var(--triple-unit) var(--triple-unit) var(--triple-unit);
`;

const FilterRow = ({
  label,
  onClick,
  onDelete,
  active,
}: {
  label: string;
  onClick: () => void;
  onDelete: () => void;
  active: boolean;
}) => {
  const [hovering, setHovering] = useState(false);
  const { up: isLaptop } = useScreenSize('laptop');

  const showDeleteBtn = !isLaptop || hovering;
  return (
    <Row
      onMouseOver={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <StyledButton
        type="button"
        onClick={onClick}
        className={classNames({ active })}
      >
        <BodyM title={label} bold className="truncate">
          {label}
        </BodyM>
      </StyledButton>
      {!!showDeleteBtn && (
        <DeleteButton type="button" onClick={onDelete}>
          <FontAwesomeIcon icon={iconTrash} />
        </DeleteButton>
      )}
    </Row>
  );
};

type Props = {
  currentUiState: CardFilters;
  filtersCount: number;
  applyFilters: (filters: CardFilters) => void;
};

export const SavedFiltersView = ({
  currentUiState,
  filtersCount,
  applyFilters,
}: Props) => {
  const { sport } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const track = useEvents();
  const [open, toggleOpen] = useToggle(false);
  const [name, setName] = useState<string>('');
  const getInteractionContext = useGetInteractionContext();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { up: isLaptop } = useScreenSize('laptop');

  const { cardFilters } = useCurrentMarketplaceUserContext();
  const [updateFilters] = useMutation(UPDATE_SAVED_FILTERS_MUTATION);

  const activeFiltersKey = useMemo(() => {
    return Object.entries(cardFilters || {}).find(([, filters]) =>
      isEqual(currentUiState, filters)
    )?.[0];
  }, [currentUiState, cardFilters]);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (!sport) return;

      updateFilters({
        variables: {
          input: { sport, filters: { [name]: currentUiState } },
          sport,
        },
      });

      track('Create Saved Search', {
        interactionContext: getInteractionContext(),
        sport,
        name,
        filters: toTrackingFilters(currentUiState),
      });

      toggleOpen();
      setName('');
    },
    [
      currentUiState,
      sport,
      name,
      toggleOpen,
      updateFilters,
      track,
      getInteractionContext,
    ]
  );

  const onSelect = useCallback(
    (key: string, filters: CardFilters) => {
      applyFilters(filters);
      track('Select Saved Search', {
        name: key,
        filters: toTrackingFilters(filters),
      });
    },
    [applyFilters, track]
  );

  const deleteFilters = useCallback(
    (key: string, filters: CardFilters) => {
      if (!sport) return;

      updateFilters({
        variables: {
          input: { sport, filters: { [key]: null } },
          sport,
        },
      });

      track('Delete Saved Search', {
        name: key,
        filters: toTrackingFilters(filters),
      });
    },
    [sport, updateFilters, track]
  );

  if (!sport || !currentUser) return null;

  const DropdownContainer = !isLaptop ? Fragment : DropdownContent;

  return (
    <>
      <Dropdown
        align="right"
        label={props => (
          <Button
            {...props}
            disableDebounce
            color={activeFiltersKey ? 'primary' : 'tertiary'}
            startIcon={<FontAwesomeIcon icon={iconBookmark} />}
            size="small"
            fullWidth={isTabletAndAbove}
          >
            {!!isTabletAndAbove && (
              <FormattedMessage {...glossary.mySearches} />
            )}
            <ChevronDownBold />
          </Button>
        )}
        gap={16}
      >
        {({ closeDropdown }) => (
          <DropdownContainer>
            <AddButton
              type="button"
              disabled={!!activeFiltersKey || !filtersCount}
              onClick={() => {
                closeDropdown();
                toggleOpen();
              }}
            >
              <FontAwesomeIcon icon={iconPlus} size="xs" />
              <BodyM bold className="truncate">
                <FormattedMessage defaultMessage="Add to Saved searches" />
              </BodyM>
            </AddButton>
            {Object.entries(cardFilters).map(([key, filters]) => (
              <FilterRow
                key={key}
                label={key}
                onClick={() => {
                  closeDropdown();
                  onSelect(key, filters);
                }}
                onDelete={() => deleteFilters(key, filters)}
                active={activeFiltersKey === key}
              />
            ))}
          </DropdownContainer>
        )}
      </Dropdown>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={open}
        onClose={toggleOpen}
        title={
          <BodyM className="text-center">
            <FormattedMessage defaultMessage="Name your Saved Search" />
          </BodyM>
        }
      >
        <DialogForm onSubmit={onSubmit}>
          <SearchInput
            autoFocus
            value={name}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
            }}
            size="medium"
          />
          <Button color="primary" size="small" type="submit">
            <FormattedMessage {...glossary.save} />
          </Button>
        </DialogForm>
      </Dialog>
    </>
  );
};
